import { observer } from "mobx-react-lite";
import { useState } from "react";

import FileContentDisplay from "./FileContentDisplay";
import { useFileOperations } from "./hooks/useFileOperations";
import MaximizeDialog from "./MaximizeDialog";
import VariableActions from "./VariableActions";
import VariableKeyInput from "./VariableKeyInput";
import VariableValueInput from "./VariableValueInput";

import { cn } from "@/lib/utils";
import { Variable } from "@/components/InputVariableConfigurator/types";
import { usePlaygroundChatSettings } from "@/components/Playground/Drawer/Chat/hooks/usePlaygroundChatSettings";
import { ChatSettingInput } from "@/components/InputVariableConfigurator/ConfiguratorContent/VariableList/InputVariable/ChatSettingInput";

interface InputVariableProps {
  variable: Variable;
  onChange: (variable: Variable) => void;
  onDelete: () => void;
  hideKey?: boolean;
  showAddFileButton?: boolean;
  className?: string;
  keyPlaceholder?: string;
  keyTooltip?: string;
  removeTooltip?: string;
  placeholder?: string;
  tooltip?: string;
  readOnly?: boolean;
  onInputRef?: (ref: HTMLInputElement | null) => void;
  onFocusNext?: () => void;
  popoverPortalContainer?: HTMLElement | null;
}

const InputVariable = observer(
  ({
    showAddFileButton = true,
    variable,
    onChange,
    onDelete,
    hideKey = false,
    className,
    keyPlaceholder,
    keyTooltip,
    removeTooltip,
    placeholder,
    tooltip = "Variable value",
    readOnly = false,
    onInputRef,
    onFocusNext,
    popoverPortalContainer,
  }: InputVariableProps) => {
    const [isMaximizeDialogOpen, setIsMaximizeDialogOpen] = useState(false);
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [isMiniEditorOpen, setIsMiniEditorOpen] = useState(false);

    const {
      fileItems,
      isFileContent,
      isProcessingFile,
      handleDeleteFile,
      handleAddFiles,
      handleSaveFile,
    } = useFileOperations({ variable, onChange });

    const { isChatMode, isChatSettingsVariableKey } = usePlaygroundChatSettings(variable.key);

    const isChatSettingsVariable = isChatMode && isChatSettingsVariableKey;

    const handleKeyInputTab = () => {
      if (!readOnly && !isFileContent) {
        setIsMiniEditorOpen(true);
      }
    };

    return (
      <div className={`${hideKey ? "grid-cols-1" : "grid-cols-2"} grid gap-x-2`}>
        {!hideKey && (
          <VariableKeyInput
            variable={variable}
            onChange={onChange}
            keyPlaceholder={keyPlaceholder}
            keyTooltip={keyTooltip}
            readOnly={readOnly}
            onInputRef={onInputRef}
            onTabPress={handleKeyInputTab}
          />
        )}
        <div className="flex flex-1 gap-x-2">
          {isChatSettingsVariable && <ChatSettingInput />}

          {!isChatSettingsVariable && isFileContent && (
            <FileContentDisplay
              fileItems={fileItems}
              isPopoverOpen={isPopoverOpen}
              setIsPopoverOpen={setIsPopoverOpen}
              onAddFiles={handleAddFiles}
              onSaveFile={handleSaveFile}
              onDeleteFile={handleDeleteFile}
              readOnly={readOnly}
              popoverPortalContainer={popoverPortalContainer}
            />
          )}
          {!isChatSettingsVariable && !isFileContent && (
            <>
              <VariableValueInput
                variable={variable}
                placeholder={placeholder}
                onChange={onChange}
                isFileContent={isFileContent}
                isProcessingFile={isProcessingFile}
                onMaximize={() => setIsMaximizeDialogOpen(true)}
                tooltip={tooltip}
                readOnly={readOnly}
                className={cn(className)}
                isMiniEditorOpen={isMiniEditorOpen}
                setIsMiniEditorOpen={setIsMiniEditorOpen}
                onFocusNext={onFocusNext}
                popoverPortalContainer={popoverPortalContainer}
              />

              <MaximizeDialog
                isOpen={isMaximizeDialogOpen}
                onOpenChange={setIsMaximizeDialogOpen}
                variable={variable}
                onChange={onChange}
                isFileContent={isFileContent}
                isProcessingFile={isProcessingFile}
                fileItems={fileItems}
                isPopoverOpen={isPopoverOpen}
                setIsPopoverOpen={setIsPopoverOpen}
                onAddFiles={handleAddFiles}
                onSaveFile={handleSaveFile}
                onDeleteFile={handleDeleteFile}
                readOnly={readOnly}
                popoverPortalContainer={popoverPortalContainer}
              />
            </>
          )}

          <VariableActions
            hasValue={!!variable.value}
            showAddFileButton={!isChatSettingsVariable && showAddFileButton}
            onAddFiles={handleAddFiles}
            onDelete={onDelete}
            removeTooltip={removeTooltip}
            readOnly={readOnly}
            popoverPortalContainer={popoverPortalContainer}
          />
        </div>
      </div>
    );
  },
);

export default InputVariable;
